import Image from "next/image";
import { useTranslation } from "@/lib/hooks/use-translation";
import SampleDataNote from "@/components/home/mockups/sample-data-note";

/** `key` names the perk under `home.mocks.tiers.perks`. */
type Pill = { key: string; tone: "blue" | "yellow" | "grey" | "green" };

type Tier = {
  name: string;
  /** Left rule of the row, from the comp. */
  accent: string;
  rank: string;
  rankColor: string;
  icon: string;
  /** Key under `home.mocks.tiers`. */
  requirementKey: string;
  perks: Pill[];
  movementKey: string;
  /** Demo figures — see the note on TIERS below. */
  members: string;
  revenue: string;
  average: string;
};

/**
 * The three rows, with invented but believable numbers.
 *
 * They used to read `999,999` members and `฿999,999,999.00` — placeholder
 * padding chosen to size the columns, and unmistakably fake to anyone looking
 * at the card, which crops all but its left third. Nothing reading the HTML
 * sees the crop: to a crawler those were simply two figures on the Jelloducks
 * home page, and an answer engine repeating them would have had Jelloducks
 * claiming a million members and a billion baht.
 *
 * So they are ordinary now — a few hundred members in the top tier, a few
 * thousand in the bottom, `revenue` exactly `members x average` so the row
 * survives being read as arithmetic. Nothing here is a claim about Jelloducks
 * or any customer, which is what `SampleDataNote` says in the markup.
 *
 * The columns are sized by `w-[5.5em]` / `w-[8.25em]` below, not by the width
 * of these strings, so a shorter number cannot disturb the layout the comp
 * draws — which is what the padding was for in the first place.
 */
const TIERS: Tier[] = [
  {
    name: "Diamond",
    accent: "#FAC5C3",
    rank: "#1",
    rankColor: "#E3A200",
    icon: "/v2/feature/tier1.png",
    requirementKey: "reqPoints",
    perks: [
      { key: "x25", tone: "blue" },
      { key: "birthdayGift", tone: "yellow" },
      { key: "ship50", tone: "grey" },
      { key: "freeItem", tone: "blue" },
    ],
    movementKey: "autoMove",
    members: "128",
    revenue: "฿184,320.00",
    average: "฿1,440",
  },
  {
    name: "Platinum",
    accent: "#FFEA97",
    rank: "#2",
    rankColor: "#97A0A8",
    icon: "/v2/feature/tier2.png",
    requirementKey: "reqSpendAndPoints",
    perks: [
      { key: "x20", tone: "blue" },
      { key: "off20", tone: "green" },
      { key: "birthdayGift", tone: "yellow" },
      { key: "ship300", tone: "grey" },
      { key: "freeItem", tone: "blue" },
    ],
    movementKey: "autoDemote",
    members: "412",
    revenue: "฿356,380.00",
    average: "฿865",
  },
  {
    name: "Gold",
    accent: "#C5E9CD",
    rank: "#3",
    rankColor: "#E37000",
    icon: "/v2/feature/tier3.png",
    requirementKey: "reqPoints",
    perks: [
      { key: "x15", tone: "blue" },
      { key: "off5", tone: "green" },
      { key: "birthdayGift", tone: "yellow" },
      { key: "freeChicken", tone: "blue" },
    ],
    movementKey: "noMove",
    members: "1,046",
    revenue: "฿606,680.00",
    average: "฿580",
  },
];

/**
 * One row's height plus the gap under it, in this file's em — the distance
 * between two slots, which is what the reordering loop translates by.
 *
 * The rows would size to 5em from their content anyway (3em of icon inside 1em
 * of padding top and bottom), but they are given the height explicitly below
 * so the pitch stays one number: a perk list that wrapped to another line in
 * some other language would otherwise make one row taller than the rest and
 * the loop would leave gaps.
 *
 * Repeated as literals in the markup — `h-[5em]`, `gap-[1.311em]` — because
 * Tailwind reads class names out of the source and a template built from these
 * would generate no CSS. Keep the two in step.
 */
const ROW_PITCH = 5 + 1.311;

const TONE: Record<Pill["tone"], string> = {
  blue: "bg-[#EBF7FF] text-[#2E99E6] outline-[#C0E5FF]",
  yellow: "bg-[#FFF8EB] text-[#E6A82E] outline-[#FFEAC0]",
  grey: "bg-[#FBFBFB] text-[#8A8A8A] outline-[#E6E6E6]",
  green: "bg-[#ECF8EF] text-[#3CA554] outline-[#C5E9CD]",
};

/**
 * TierRowsMock — the loyalty-tier table behind the "ลูกค้ากลับมาใช้บริการซ้ำ"
 * card in section 4.
 *
 * The row is 747.83px wide against a 360px card, so all but its left third is
 * cropped away — that is the comp's intent, and the reason this is built as
 * live UI rather than a screenshot: the part that survives the crop has to
 * stay sharp at whatever width the card ends up.
 *
 * One container query drives the whole table: `1em` is the comp's 9.15px at
 * its natural width, so every number below is a comp pixel over 9.15 and the
 * table scales as one piece.
 *
 * `bento-media` is the section's entrance class (globals.css) — the rows carry
 * it themselves so they can arrive one after another rather than as a block.
 *
 * Once they have arrived the table keeps reordering itself: the bottom row
 * climbs over the other two to the top, they each step down a place, and it
 * repeats. That lives in `.tier-cycle` (globals.css) and rides on a wrapper of
 * its own, because the entrance and the loop both write `transform` and would
 * otherwise overwrite each other. The wrapper is what carries `ROW_PITCH` into
 * the loop as `--tier-pitch`, the distance a row travels to reach the next
 * slot.
 */
export default function TierRowsMock() {
  const { t } = useTranslation();

  return (
    <div
      className="w-full"
      // See SampleDataNote: Google's opt-out for the figures below, so a demo
      // screen cannot be quoted back as a Jelloducks result.
      data-nosnippet
      style={{ containerType: "inline-size" }}
    >
      <SampleDataNote />
      <div className="flex flex-col gap-[1.311em] text-[1.2236cqw]">
        {TIERS.map((tier, i) => (
          <div
            key={tier.name}
            style={
              {
                // Which of the three slot tracks this row runs; see the
                // `tier-cycle-*` keyframes in globals.css.
                "--tier-cycle": `tier-cycle-${["a", "b", "c"][i]}`,
                "--tier-pitch": `${ROW_PITCH}em`,
              } as React.CSSProperties
            }
            className="tier-cycle"
          >
            <div
              style={
                {
                  "--media-d": `${0.1 + i * 0.12}s`,
                  // Per-row and therefore inline: a Tailwind colour utility is
                  // a build-time class, and this value comes from the data.
                  borderLeftColor: tier.accent,
                } as React.CSSProperties
              }
              className="bento-media flex h-[5em] items-center justify-between rounded-[1.249em] border-l-[0.25em] border-solid bg-white p-[1em] shadow-[-0.219em_0.219em_0.973em_rgba(0,0,0,0.05)]"
            >
              <span
                className="flex w-[3em] shrink-0 items-center justify-center rounded-[0.75em] px-[0.75em] py-[0.375em] text-[1em]/[1.5em] font-bold text-white"
                style={{ background: tier.rankColor }}
              >
                {tier.rank}
              </span>

              <span className="flex w-[18.12em] shrink-0 items-center gap-[0.75em]">
                <Image
                  src={tier.icon}
                  alt=""
                  width={110}
                  height={110}
                  sizes="30px"
                  className="size-[3em] shrink-0 rounded-[0.625em]"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-[1em]/[1.5em] font-semibold text-black">
                    {tier.name}
                  </span>
                  <span className="text-[0.874em]/[1.249em] text-black/50">
                    {t(`home.mocks.tiers.${tier.requirementKey}`)}
                  </span>
                </span>
              </span>

              <span className="flex w-[17.5em] shrink-0 flex-wrap content-center items-center gap-[0.5em]">
                {tier.perks.map((perk) => (
                  <span
                    key={perk.key}
                    className={`rounded-full px-[0.5em] text-[0.75em]/[1.249em] font-semibold outline outline-[0.062em] -outline-offset-[0.062em] ${TONE[perk.tone]}`}
                  >
                    {t(`home.mocks.tiers.perks.${perk.key}`)}
                  </span>
                ))}
              </span>

              <span className="w-[7.5em] shrink-0 text-[0.874em]/[1.249em] text-black/60">
                {t(`home.mocks.tiers.${tier.movementKey}`)}
              </span>

              <span className="flex w-[5.5em] shrink-0 items-center justify-center gap-[0.375em]">
                <span className="text-[1em]/[1.5em] font-medium text-black">
                  {tier.members}
                </span>
                <span className="text-[0.874em]/[1.249em] font-medium text-black/50">
                  {t("home.mocks.tiers.people")}
                </span>
              </span>

              <span className="flex shrink-0 flex-col items-end">
                <span className="w-[8.25em] text-right text-[1em]/[1.5em] font-semibold text-[#43B75D]">
                  {tier.revenue}
                </span>
                <span className="text-[0.75em]/[1.249em] text-black/50">
                  {t("home.mocks.tiers.average", { amount: tier.average })}
                </span>
              </span>

              {/* Enabled switch, drawn rather than an <input>: nothing here is
                operable, it is a picture of the product. */}
              <span aria-hidden className="relative h-[2em] w-[4em] shrink-0">
                <span className="absolute left-[0.455em] top-[0.184em] h-[1.65em] w-[3em] rounded-full bg-[#43B75D]">
                  <span className="absolute left-[1.5em] top-[0.15em] size-[1.35em] rounded-full bg-white shadow-[0_0.15em_0.3em_rgba(39,39,39,0.1)]" />
                </span>
              </span>

              <span
                aria-hidden
                className="size-[2em] shrink-0 rounded-[0.4em] bg-white outline outline-[0.05em] -outline-offset-[0.05em] outline-[#E6E6E6]"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
